import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Grid, Button } from "@mui/material";
import LoaderWrapper from "../../../wrapper/LoaderWrapper";
import { useAsync } from "../../../../hooks/useAsync";
import usePaginationWithSearchParams from "../../../../hooks/usePaginationWithSearchParams";
import useDialog from "../../../../hooks/useDialog";
import TableService from "../../../../services/tableService";
import FilterTable from "../../../table/FilterTable";
import { useEmptyArrayMemo } from "../../../../hooks/useEmptyArrayMemo";
import { useSearchParams } from "react-router-dom";
import useFilterSearchParams from "../../../../hooks/useFilterSearchParams";
import { centerVericalAlignIconStyle } from "../../../../helpers/styles";
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import SearchButton from "../../../button/SearchButton/SearchButton";
import FilterDialog from "../../../base/FilterDialog";
import UniversalFilterForm from "../../../form/UniversalFilterForm";
import useDialogWithId from "../../../../hooks/useDialogWithId";
import { POST_CATEGORY_TABLE_CONFIG } from "./TableConfig";
import useCorrespondenceAdminService from "../../../../services/correspondenceAdminService";
import CreatePostCategoryDialog from "../../../dialog/CreatePostCategoryDialog/CreatePostCategoryDialog";

const POST_CATEGORY_FILTER_PREFIX = "post_category";

const CorrespondenceAdminPostCategoryTab = (props) => {
  const { t } = useTranslation();
  const [postCategoriesLocal, setPostCategoriesLocal] = useState([]);
  const [countRecords, setCountRecords] = useState();
  const [searchParams, setSearchParams] = useSearchParams();
  const [postCategoryTableSearchParams, setPostCategoryTableSearchParams] = useState({});

  const { setNewItemSearchParamsIfAreChanged, clearSearchParamsByFilterPrefix } =
    useFilterSearchParams(searchParams);

  const { emptyArrayMemo } = useEmptyArrayMemo();
  const { getPostCategoryList } = useCorrespondenceAdminService(); 


  const onCleanFiltersInFilterDialog = () => {
    setSearchParams(clearSearchParamsByFilterPrefix(POST_CATEGORY_FILTER_PREFIX));
  };

  const [
    openPostCategoryDialog,
    postCategoryChangeId,
    onOpenPostCategoryDialog,
    onClosePostCategoryDialog,
    exportChangeCategoryStatus
  ] = useDialogWithId();

  const [openFilterDialog, onOpenFilterDialog, onCloseFilterDialog] =
    useDialog();

  const {
    page,
    pageSize,
    handleChangePageWithSearchParams,
    handleChangeRowsPerPage,
    resetPageNumber,
  } = usePaginationWithSearchParams(POST_CATEGORY_FILTER_PREFIX);

  const postCategoryData = useAsync(
    () => getPostCategoryList(postCategoryTableSearchParams),
    [postCategoryTableSearchParams]
  );

  useEffect(() => {
    if (postCategoryData.loading) {
      return;
    }
    setPostCategoriesLocal(postCategoryData.value.results);
    setCountRecords(postCategoryData.value.count);
  }, [postCategoryData.loading]);

  const isLoading = postCategoriesLocal === undefined || props.filteringData.loading;

  const handleOpenPostCategoryDialog = useCallback(
    (e, postCategoryId) => {
      e.stopPropagation();
      onOpenPostCategoryDialog(postCategoryId);
    },
    []
  );

  const refreshTable = () => {
    setPostCategoriesLocal([]);
    postCategoryData.refetch();
  };

  const data = React.useMemo(
    () =>
      TableService.getPreparedDataForCollapsibleTable(
        POST_CATEGORY_TABLE_CONFIG,
        postCategoriesLocal ? postCategoriesLocal : emptyArrayMemo,
        emptyArrayMemo,
        emptyArrayMemo,
        handleOpenPostCategoryDialog,
      ),
    [postCategoriesLocal, POST_CATEGORY_TABLE_CONFIG]
  );

  useEffect(() => {
    setNewItemSearchParamsIfAreChanged(
      POST_CATEGORY_FILTER_PREFIX,
      postCategoryTableSearchParams,
      setPostCategoryTableSearchParams
    );
  }, [searchParams]);

  if (isLoading) return <LoaderWrapper showLoader={true} />;

  return (
    <Grid
      item
      container
      direction="row"
      justifyContent="flex-start"
      alignItems="center"
      rowSpacing={1}
    >
      <Grid item xs={12} sm={2}>
        <Button
          fullWidth
          variant="contained"
          size="small"
          startIcon={<ControlPointOutlinedIcon sx={centerVericalAlignIconStyle} />}
          onClick={(e) => onOpenPostCategoryDialog(undefined)}
        >
          {t("tabs.correspondence_admin.add_post_category")}
        </Button>
      </Grid>
      <Grid item sm={9} />
      <Grid item xs={12} sm={1}>
        <SearchButton onClickSearch={onOpenFilterDialog} />
      </Grid>
      <Grid item xs={12}>
        <FilterTable
          data={data}
          showCheckbox={false}
          showCleanFilterIcon={true}
          countRecords={countRecords}
          page={page}
          onPageChange={handleChangePageWithSearchParams}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          resetPageNumber={resetPageNumber}
          filteringData={props.filteringData}
          style={{ maxHeight: "vh" }}
          tableConfig={POST_CATEGORY_TABLE_CONFIG}
          showContextMenu={false}
          filterPrefix={POST_CATEGORY_FILTER_PREFIX}
          headersConfig={TableService.getHeadersConfigForCollapsibleTable(
            POST_CATEGORY_TABLE_CONFIG,
            props.filteringData || emptyArrayMemo,
            emptyArrayMemo,
            emptyArrayMemo,
          )}
        />
      </Grid>
      {openFilterDialog &&
        <FilterDialog
          open={openFilterDialog}
          onCleanFilters={onCleanFiltersInFilterDialog}
          onClose={onCloseFilterDialog}
          resetPageNumber={resetPageNumber}
          filterPrefix={POST_CATEGORY_FILTER_PREFIX}
          filterForm={
            <UniversalFilterForm
              filteringData={props.filteringData || emptyArrayMemo}
              filtersConfig={POST_CATEGORY_TABLE_CONFIG}
              includeOpenCloseFilter={false}
            />
          }
        />
      }
      {openPostCategoryDialog &&
        <CreatePostCategoryDialog
          categoryId={postCategoryChangeId}
          open={openPostCategoryDialog}
          onClose={onClosePostCategoryDialog}
          onRefetchData={refreshTable}
          filteringData={props.filteringData}
        />
      }
    </Grid>
  );
};

CorrespondenceAdminPostCategoryTab.propTypes = {
  pageName: PropTypes.string,
  readOnly: PropTypes.bool,
};

CorrespondenceAdminPostCategoryTab.defaultProps = {};

export default CorrespondenceAdminPostCategoryTab;
